import {
    Getter,
    ComputedGetters,
} from '@app/store/StoreUtil';
import { SportId } from '@core/constants';
import OfferEvent from '@core/models/offer/OfferEvent';
import { CategoryWithCompetitions } from '@core/models/struct/CategoryWithCompetitions';
import Category from '@models/struct/Category';
import Market from '@models/struct/Market';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import MarketTree from '@models/struct/MarketTree';
import OddType from '@models/struct/OddType';
import Sport from '@models/struct/Sport';
import Tournament from '@models/struct/Tournament';
import { OddValueChange } from '@store/modules/data/sportOffer/enums';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';

export interface ISuperOfferCollection {
    matchIds: OfferEvent['id'][];
    primaryMarketId: number;
}
export type ISuperOfferCollections = Record<string, ISuperOfferCollection>;

export interface IPromotionalEventGroup {
    id: string;
    title: string;
    footer: string;
    primaryMarketAttribute: string;
    type: string;
    events: OfferEvent[];
}

export type OddValueDynamics = Record<number, Record<string, OddDynamics>>;

export interface OddDynamics {
    oddDirection: OddValueChange;
    created: Date;
}

export interface PromotionGroupPractical {
    id: string;
    order: string;
    events: OfferEvent[];
    nameKey: string;
    primaryMarketAttribute: string;
    type: string;
    footerKey: string;
}

export type HotTournament = {
    link: string;
    title: string;
    locations: HotTournamentLocation[];
    tournamentIds: number[];
    sportId: SportId;
    categoryId: number;
    flagId: number;
    sportTag?: string;
    colors: {
        from: string;
        to: string;
    };
    iconUrl: string;
};

export const HotTournamentLocation = {
    Home: 'home',
    Prematch: 'prematch',
} as const;
export type HotTournamentLocation = typeof HotTournamentLocation[keyof typeof HotTournamentLocation];

export type PopularSuperbet = {
    eventID: number,
    oddID: string,
};

export type PopularSuperbetV2DTO = OfferEventDto & { popularSuperbetId: string };

export type PopularSuperbetV2 = {
    event: OfferEvent,
    popularSuperbetId: string,
};

export interface ISportOfferState {
    sports: Record<number, Sport>;
    categories: Record<number, Category>;
    tournaments: Record<number, Tournament>;
    markets: Record<number, Market>;
    marketGroupInfos: Record<number, MarketGroupInfo>;
    competitionTreeBySportId: Record<number, CategoryWithCompetitions[]>;
    oddTypes: Record<number, OddType>;
    marketTree: MarketTree;
    events: Record<string, OfferEvent>;
    oddValueDynamics: OddValueDynamics;
    topTenEvents: number[];
    topEventIds: number[];
    isOfferStarting: boolean;
    isOfferReady: boolean;
    areLiveEventsFetched: boolean;
    isStructFetched: boolean;
    isCompetitionTreeFetchBySportId: Record<number, boolean>;
    featuredTournaments: Record<number, any>;
    liveEventsCount: number | null;
    promotionalEvents: ISuperOfferCollections;
    // for each change via socket increment this property
    // ideal when we want simple watch of events, but we don't
    // need detail subscription
    // number is not important, see mutator
    allChangesIncrementId: number;
    areSubscriptionsSet: boolean;
    isEventsSubscriptionSet: boolean;
    isOfferFetched: boolean;
    isOfferFetchedBySport: Record<number, boolean>;
    isSuperOfferFetched: boolean;
    areTopTenEventsFetched: boolean;
    areTopEventIdsFetched: boolean;
    isFirstDailyOfferSync: boolean,
    isFirstStructSync: boolean,
    sportDatesWithActiveOffer: Record<number, Date[]>;
    sportsWithEvents: number[];
    popularSuperbets: PopularSuperbet[];
    popularSuperbetsV2: PopularSuperbetV2[];
}

type ISportOfferGetter<T> = Getter<ISportOfferState, SportOfferGetters, T>;

export interface Getters {
    sports: ISportOfferGetter<ISportOfferState['sports']>;
    outrights: ISportOfferGetter<OfferEvent[]>;
    tournaments: ISportOfferGetter<ISportOfferState['tournaments']>;
    markets: ISportOfferGetter<ISportOfferState['markets']>;
    categories: ISportOfferGetter<ISportOfferState['categories']>;
    oddTypes: ISportOfferGetter<ISportOfferState['oddTypes']>;
    eventsMap: ISportOfferGetter<ISportOfferState['events']>;
    events: ISportOfferGetter<OfferEvent[]>;
    topTenEvents: ISportOfferGetter<OfferEvent[]>;
    topEvents: ISportOfferGetter<OfferEvent[]>;
    promotionalEvents: ISportOfferGetter<PromotionGroupPractical[]>;
    isOfferStarting: ISportOfferGetter<ISportOfferState['isOfferStarting']>;
    isOfferReady: ISportOfferGetter<ISportOfferState['isOfferReady']>;
    featuredItems: ISportOfferGetter<Tournament[]>;
    liveEventsCount: ISportOfferGetter<ISportOfferState['liveEventsCount']>;
    isStructFetched: ISportOfferGetter<ISportOfferState['isStructFetched']>;
    allChangesIncrementId: ISportOfferGetter<ISportOfferState['allChangesIncrementId']>;
    areSubscriptionsSet: ISportOfferGetter<ISportOfferState['areSubscriptionsSet']>;
    isOfferFetched: ISportOfferGetter<ISportOfferState['isOfferFetched']>;
    isSuperOfferFetched: ISportOfferGetter<ISportOfferState['isSuperOfferFetched']>;
    areTopTenEventsFetched: ISportOfferGetter<ISportOfferState['areTopTenEventsFetched']>;
    areTopEventIdsFetched: ISportOfferGetter<ISportOfferState['areTopEventIdsFetched']>;
    oddValueDynamics: ISportOfferGetter<ISportOfferState['oddValueDynamics']>;
    isFirstDailyOfferSync: ISportOfferGetter<ISportOfferState['isFirstDailyOfferSync']>;
    isFirstStructSync: ISportOfferGetter<ISportOfferState['isFirstStructSync']>;
    popularSuperbets: ISportOfferGetter<ISportOfferState['popularSuperbets']>;
    popularSuperbetsV2: ISportOfferGetter<ISportOfferState['popularSuperbetsV2']>;
    [key: string]: ISportOfferGetter<any>; // to satisfy vuex typings
}

export type SportOfferGetters = ComputedGetters<Getters>;
